import type { Location, Store, StoreCapabilities, StoreRole } from "@hobbyshopify/backend-client";
import { Rx } from "subtree.js/core";
import type { OnboardingStep } from "../core/primitives/onboarding";

/**
 * Whether the signed-in area can render yet. `ready` means the shops are known
 * and, when there is one, its branches are too: the onboarding gate needs both
 * to say which step, if any, is missing.
 */
export type StoreGate = "loading" | "error" | "ready";

export class ShellState {
  readonly storeGate = new Rx<StoreGate>("loading");
  readonly storeError = new Rx<string | null>(null);
  readonly selectedStore = new Rx<Store | null>(null);
  /** The caller's role in the selected shop, mirrored from `selectedStore.myRole`. */
  readonly role = new Rx<StoreRole | null>(null);
  /** What HobbyScan has switched on for the selected shop; null until it answers, like `role`. */
  readonly capabilities = new Rx<StoreCapabilities | null>(null);
  /** The capabilities could not be read: the gates stand aside and the server has the last word. */
  readonly capabilitiesFailed = new Rx(false);
  readonly locationCount = new Rx(0);
  /** The counter the app is working at; null for a shop with no location yet. */
  readonly selectedLocation = new Rx<Location | null>(null);
  /** No shop × location is chosen and there is more than one to choose from: the picker owns the screen. */
  readonly needsWorkspace = new Rx(false);
  /** More than one shop × location exists, so the header offers to switch. */
  readonly canSwitchWorkspace = new Rx(false);
  /** The step still missing, or null when the shop is set up. Meaningful only once `storeGate` is `ready`. */
  readonly onboardingStep = new Rx<OnboardingStep | null>(null);
  /** Carts waiting on the merchant: the badge in the nav. */
  readonly pendingCarts = new Rx(0);
  /** The shop has a show running, which can be a co-host on another device entirely. */
  readonly liveNow = new Rx(false);
  /** This device's camera is publishing. */
  readonly onAir = new Rx(false);
  readonly mobileMenuOpen = new Rx(false);
}

export abstract class ShellActions {
  abstract signOut(): Promise<void>;
  abstract retryStores(): void;
  /** What `?storeId=` asks for; the router calls this as the URL changes. */
  abstract requestStore(storeId: string | null): void;
  /** Back to the shop × location picker. */
  abstract switchWorkspace(): void;
  /** An onboarding answer kept in the browser changed (payments deferred, the selling path): resolve the step again. */
  abstract onboardingAnswered(): void;
  abstract toggleMobileMenu(): void;
  abstract closeMobileMenu(): void;
}
